import { create } from "zustand";
import { QdaDocument } from "../types";
import { getSettingValue } from "../settings/settingsStore";

function defaultEditMode(): boolean {
  const value = getSettingValue("general", "defaultEditMode");
  return typeof value === "boolean" ? value : true;
}

interface DocumentStore {
  document: QdaDocument | null;
  loading: boolean;
  error: string | null;
  editMode: boolean;

  setDocument: (doc: QdaDocument) => void;
  updateParagraph: (paraId: string, text: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setEditMode: (enabled: boolean) => void;
  clear: () => void;
}

export const useDocumentStore = create<DocumentStore>((set: any) => ({
  document: null,
  loading: false,
  error: null,
  // Provisional — the configured default is applied by applyDefaultEditMode()
  // at app load. Reading the setting *here* would run during this module's
  // evaluation which, via the settings registry, forms a settingsStore <->
  // documentStore import cycle and throws a temporal-dead-zone ReferenceError
  // ("can't access 'useSettingsStore' before initialization") when settingsStore
  // is the module that enters the cycle first (as it is once App is lazy-loaded
  // and i18n/theme evaluate settings at boot). Matches general.defaultEditMode's
  // own default (true), so unchanged for the common case.
  editMode: true,

  setDocument: (doc: QdaDocument) => set({ document: doc, error: null }),
  updateParagraph: (paraId: string, text: string) =>
    set((state: DocumentStore) => {
      if (!state.document) return {};
      const updated = { ...state.document };
      updated.paragraphs = updated.paragraphs.map((p) =>
        p.id === paraId ? { ...p, text } : p
      );
      return { document: updated };
    }),
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),
  setEditMode: (enabled: boolean) => set({ editMode: enabled }),
  clear: () => set({ document: null, error: null, editMode: defaultEditMode() }),
}));

/**
 * Apply the user's "open documents in edit mode" default to the store.
 *
 * Kept out of the store initializer above so that no setting is read while this
 * module is first evaluated (see the note on `editMode`). Idempotent — call
 * once at app load, before the editor first renders. By the time the (lazy) app
 * bundle runs this, the settings store is fully initialized, so the read is safe.
 */
let editModeDefaultApplied = false;
export function applyDefaultEditMode(): void {
  if (editModeDefaultApplied) return;
  editModeDefaultApplied = true;
  useDocumentStore.setState({ editMode: defaultEditMode() });
}
